import React, { useState } from 'react';
import { Box, IconButton, Dialog, DialogTitle, DialogContent, Typography, Divider } from '@mui/material';
import { Add as AddIcon, Extension } from '@mui/icons-material';
import { emailBuilderBlockBackground } from './blockStyles';

// Block types for selection dialog
const BLOCK_TYPES = [
  { type: 'heading', label: 'Heading' },
  { type: 'paragraph', label: 'Paragraph' },
  { type: 'image', label: 'Image' },
  { type: 'button', label: 'Button' },
  { type: 'divider', label: 'Divider' },
  { type: 'spacer', label: 'Spacer' },
  { type: 'avatar', label: 'Avatar' },
  { type: 'container', label: 'Container' },
  { type: 'columns', label: 'Column (2-4)' }
] as const;

export const AnimatedDashOverlay: React.FC<{ color: string }> = ({ color }) => (
  <Box sx={{
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    '@keyframes dashShift': {
      '0%': { strokeDashoffset: 0 },
      '100%': { strokeDashoffset: -64 }
    }
  }}>
    <svg width="100%" height="100%" preserveAspectRatio="none">
      <rect x="1" y="1" width="calc(100% - 2px)" height="calc(100% - 2px)" fill="none" stroke={color} strokeWidth="2" strokeDasharray="8 8" style={{ animation: 'dashShift 1.2s linear infinite' }} />
    </svg>
  </Box>
);

// Block Selection Dialog Component
const BlockSelectionDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSelect: (type: string) => void;
}> = ({ open, onClose, onSelect }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ pb: 1 }}>
      <Typography variant="h6" component="span">Select Block Type</Typography>
    </DialogTitle>
    <Divider />
    <DialogContent sx={{ pt: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
        {BLOCK_TYPES.map(({ type, label }) => (
          <Box
            key={type}
            onClick={() => {
              onSelect(type);
              onClose();
            }}
            sx={{
              px: 2,
              py: 1.5,
              border: '1px solid',
              borderRadius: 2,
              borderColor: 'divider',
              backgroundColor: emailBuilderBlockBackground,
              cursor: 'pointer',
              transition: 'all 150ms ease',
              '&:hover': {
                backgroundColor: 'primary.main',
                borderColor: 'primary.main',
                color: 'common.white',
                transform: 'scale(1.02)',
                '& .MuiSvgIcon-root': {
                  color: 'common.white'
                }
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Extension fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </DialogContent>
  </Dialog>
);

export const RootDropZone: React.FC<{ onDrop: (data: any) => void; isDragging: boolean; }> = ({ onDrop, isDragging }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleBlockSelect = (type: string) => {
    // Create the same data structure as drag-and-drop
    onDrop({ kind: 'new', type });
  };

  return (
    <>
      <Box
        sx={{
          my: 1,
          height: 48,
          borderRadius: 1,
          backgroundColor: 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          fontSize: 12,
          textTransform: 'uppercase',
          position: 'relative',
          border: '1px dashed',
          borderColor: isHovered ? 'primary.main' : 'divider',
          transition: 'all 150ms ease',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'rgba(25, 118, 210, 0.04)'
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); Object.assign((e.currentTarget as HTMLElement).style, { backgroundColor: 'rgba(64,224,208,0.08)' }); }}
        onDragLeave={(e) => { e.stopPropagation(); Object.assign((e.currentTarget as HTMLElement).style, { backgroundColor: 'transparent' }); }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          Object.assign((e.currentTarget as HTMLElement).style, { backgroundColor: 'transparent' });
          const raw = e.dataTransfer.getData('application/json');
          if (!raw) return;
          onDrop(JSON.parse(raw));
        }}
      >
        {isDragging && <AnimatedDashOverlay color="#40E0D0" />}
        
        {/* Plus button - always visible on hover */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setDialogOpen(true);
          }}
          sx={{
            opacity: isHovered ? 1 : 0,
            transform: isHovered ? 'scale(1)' : 'scale(0.8)',
            transition: 'all 150ms ease',
            backgroundColor: 'primary.main',
            color: 'common.white',
            width: 28,
            height: 28,
            '&:hover': {
              backgroundColor: 'primary.dark',
              transform: 'scale(1.1)'
            }
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>

        {/* Text label - hide when hovered */}
        {!isHovered && (
          <Box sx={{ 
            position: 'absolute',
            pointerEvents: 'none', 
            px: 1, 
            py: 0.5, 
            color: 'text.disabled',
            fontSize: 10
          }}>
            Drop block here or click +
          </Box>
        )}
      </Box>

      <BlockSelectionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={handleBlockSelect}
      />
    </>
  );
};

export const InnerDropZone: React.FC<{ onDrop: (data: any) => void; isDragging: boolean; onDropComplete?: () => void; }> = ({ onDrop, isDragging, onDropComplete }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleBlockSelect = (type: string) => {
    // Create the same data structure as drag-and-drop
    onDrop({ kind: 'new', type });
    onDropComplete?.();
  };

  return (
    <>
      <Box
        sx={{
          my: 1,
          height: 48,
          borderRadius: 1,
          backgroundColor: 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          fontSize: 12,
          textTransform: 'uppercase',
          position: 'relative',
          border: '1px dashed',
          borderColor: isHovered ? 'primary.main' : 'divider',
          transition: 'all 150ms ease',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'rgba(25, 118, 210, 0.04)'
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); Object.assign((e.currentTarget as HTMLElement).style, { backgroundColor: 'rgba(64,224,208,0.08)' }); }}
        onDragLeave={(e) => { e.stopPropagation(); Object.assign((e.currentTarget as HTMLElement).style, { backgroundColor: 'transparent' }); }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          Object.assign((e.currentTarget as HTMLElement).style, { backgroundColor: 'transparent' });
          const raw = e.dataTransfer.getData('application/json');
          if (!raw) return;
          onDrop(JSON.parse(raw));
          onDropComplete?.();
        }}
      >
        {isDragging && <AnimatedDashOverlay color="#40E0D0" />}
        
        {/* Plus button - always visible on hover */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setDialogOpen(true);
          }}
          sx={{
            opacity: isHovered ? 1 : 0,
            transform: isHovered ? 'scale(1)' : 'scale(0.8)',
            transition: 'all 150ms ease',
            backgroundColor: 'primary.main',
            color: 'common.white',
            width: 28,
            height: 28,
            '&:hover': {
              backgroundColor: 'primary.dark',
              transform: 'scale(1.1)'
            }
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>

        {/* Text label - hide when hovered */}
        {!isHovered && (
          <Box sx={{ 
            position: 'absolute',
            pointerEvents: 'none', 
            px: 1, 
            py: 0.5, 
            color: 'text.disabled',
            fontSize: 10
          }}>
            Drop block here or click +
          </Box>
        )}
      </Box>

      <BlockSelectionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={handleBlockSelect}
      />
    </>
  );
};
